/**
 * Deeply compares two instances, checking if all properties of the interface T instance
 * exist in the model implementation of T instance and have equal values.
 *
 * @param superClassInstance The superclass (model instance) to compare against.
 * @param subClassInstance The subclass (interface instance) to compare.
 * @returns A boolean indicating whether the subclass instance is deeply equal to the superclass instance.
 */
export function deepCompare<I extends object, Super extends I, Sub extends I>(superClassInstance: Super, subClassInstance: Sub): boolean {
	if (!superClassInstance || !subClassInstance) return false;
	// Check if all subClassInstance properties exist in superClassInstance and are equal to them
	return Object.keys(subClassInstance).every(key => {
		const subClassInstanceValue = subClassInstance[key];
		const superClassInstanceValue = superClassInstance[key];

		if (Array.isArray(subClassInstanceValue)) {
			// Handle array comparison separately
			return isArrayEqual(superClassInstanceValue, subClassInstanceValue);
		}

		if (typeof subClassInstanceValue === 'object' && subClassInstanceValue !== null) {
			return deepCompare(superClassInstanceValue, subClassInstanceValue);
		}

		return superClassInstanceValue === subClassInstanceValue;
	});
}

function isArrayEqual(arr1: any[], arr2: any[]): boolean {
	if (arr1.length !== arr2.length) {
		return false;
	}

	for (let i = 0; i < arr1.length; i++) {
		if (typeof arr1[i] === 'object' && arr1[i] !== null) {
			if (!deepCompare(arr1[i], arr2[i])) {
				return false;
			}
		} else if (arr1[i] !== arr2[i]) {
			return false;
		}
	}

	return true;
}
